import { useRef, useState, forwardRef, useEffect, useMemo } from "react";
import { motion } from "motion/react";
import { useRockHallTV } from "@/providers/RockHallTVContext";
import { ensurePostsAreRHTVVideos, ensurePostsAreInductees, getYoutubeThumbnail } from "@/core/utils";
import { getBooleanParam } from '@/utils/urlParams';
import { useSoundContext } from "@/providers/SoundContext";
import { useGlobalVolume } from "@/hooks/useGlobalVolume";
import { ANIMATION_CONFIG, MODAL_TIMINGS } from "../constants";

// Components
import { YoutubeEmbed } from "../../VideoEmbed/YoutubeEmbed";
import { YTRef } from "../../VideoEmbed/YoutubeEmbed/YoutubeEmbed";
import PlayerUI from "./components/PlayerUI";
import VideoTray from "./components/VideoTray";
import WebGLDistortion from "./components/WebGLDistortion";

// Styles
import styles from "./RHTV-Modal.module.scss";
import className from "classnames/bind";
const cx = className.bind(styles);

// Debug
import { useDebug } from "@/utils/useDebugLog";
const { log } = useDebug({ name: 'RHTV-Modal', bgColor: '#F2CB05', enabled: false });

// Assets
import PauseScreenImage from '@/assets/images/rhtv/generic-pause.jpg';
import VideoStatic01 from '@/assets/videos/rhtv/static-transition-01.mp4';
import VideoStatic02 from '@/assets/videos/rhtv/static-transition-02.mp4';
import VideoStatic03 from '@/assets/videos/rhtv/static-transition-03.mp4';

// Array of transition videos to cycle through
const TRANSITION_VIDEOS = [VideoStatic01, VideoStatic02, VideoStatic03];

// ----------------------------------------

interface RHTV_ModalProps {
  borderRadius?: number; // REM value for modal border radius
  onCloseComplete?: () => void;
  onContentSizeChange?: (dimensions: { width: number; height: number; x: number; y: number }) => void;
  onShouldExit?: (shouldExit: boolean) => void;
  isPortraitMode?: boolean;
  isTouchDevice?: boolean;
  trayButtonLabels: {
    videoTrayOpen: string;
    videoTrayClose: string;
  };
  rrtvAriaLabels: RockHallTvContent['rrtvAriaLabels'];
}

const RHTV_Modal = forwardRef<HTMLDivElement, RHTV_ModalProps>(({
  borderRadius = 5,
  onCloseComplete,
  onContentSizeChange,
  onShouldExit,
  isPortraitMode,
  isTouchDevice = false,
  trayButtonLabels,
  rrtvAriaLabels
}, ref) => {

  const contentRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<HTMLDivElement>(null);
  const trayRowWrapperRef = useRef<HTMLDivElement>(null);
  const pauseImageRef = useRef<HTMLImageElement>(null);
  const [playerDimensions, setPlayerDimensions] = useState<{ width: number; height: number } | null>(null);
  const [originalPlayerHeight, setOriginalPlayerHeight] = useState<number | null>(null);
  const [trayHeight, setTrayHeight] = useState(0);

  const isDebugMode = getBooleanParam('debug');

  const {
    channels,
    activeChannel,
    activeVideo,
    setShowModal,
    setActiveChannel,
    setActiveVideo,
    updateRHTVRoute
  } = useRockHallTV();

  const { rrtvSoundEnabled, globalVolume } = useSoundContext();

  const {
    title: defaultVideoTitle,
    rhtvVideoContent: {
      videoTitle: videoTitleOverride,
      youtubeVideoId,
      externalYoutubeLink,
      // thumbnail,
      artists,
    } = {},
  } = activeVideo || {};
  const { title: channelTitle } = activeChannel || {};
  const videoTitle = useMemo(() => videoTitleOverride || defaultVideoTitle, [videoTitleOverride, defaultVideoTitle]);

  // Track YouTube player state - THREE states: null (unknown), true (playing), false (paused)
  // Start as null because we don't know what YouTube will do (autoplay may work or be blocked)
  const [isPlaying, setIsPlaying] = useState<boolean | null>(null);
  // Track if user paused via our custom button (to allow YouTube interaction while paused)
  const [userPausedVideo, setUserPausedVideo] = useState(false);
  // Track if the current video has started playing at least once
  const [hasVideoStarted, setHasVideoStarted] = useState(false);
  // Track if user has interacted with the current video (clicked to play)
  const userHasInteractedRef = useRef(false);
  // Track if modal should start exit animation
  const [shouldExit, setShouldExit] = useState(false);
  // Track if we're closing the modal (prevents pause screen from appearing)
  const [isClosing, setIsClosing] = useState(false);
  // Track if we're showing the transition video
  const [isShowingTransition, setIsShowingTransition] = useState(false);
  // Store the next video to show after transition
  const [nextVideo, setNextVideo] = useState<typeof activeVideo | null>(null);
  // Track which transition video to use next (cycles through 0, 1, 2)
  const transitionIndexRef = useRef<number>(0);

  // --------------------

  // Notify parent when exit is triggered
  useEffect(() => {
    if (shouldExit) {
      onShouldExit?.(true);
    }
  }, [shouldExit, onShouldExit]);

  // Get channel videos
  const channelVideos = useMemo(() => {
    const videos = activeChannel?.rhtvChannelContent?.rhtvVideos;
    return videos ? ensurePostsAreRHTVVideos(videos) : [];
  }, [activeChannel]);

  // Current video index in channel
  const currentVideoIndex = useMemo(() =>
    channelVideos.findIndex(video => video.slug === activeVideo?.slug),
    [channelVideos, activeVideo]
  );

  // Current channel index
  const currentChannelIndex = useMemo(() =>
    channels.findIndex(channel => channel.slug === activeChannel?.slug),
    [channels, activeChannel]
  );

  // Clear active channel and video on unmount
  useEffect(() => {
    return () => {
      setActiveChannel(null);
      setActiveVideo(null);
    }
  }, []);

  // -------------------- Handlers
  //#region Handlers

  const handleClose = () => {
    log('handleClose');

    // Mark that we're closing to prevent pause screen from appearing
    setIsClosing(true);

    // Fade out YouTube audio before closing
    const player = ytRef.current?.getPlayer();
    if (player) {
      try {
        const currentVolume = player.getVolume();
        const fadeDuration = MODAL_TIMINGS.AUDIO_FADE_OUT_DURATION;
        const fadeSteps = MODAL_TIMINGS.AUDIO_FADE_OUT_STEPS;
        const stepDuration = fadeDuration / fadeSteps;
        const volumeStep = currentVolume / fadeSteps;

        // Fade out the volume gradually
        let step = 0;
        const fadeInterval = setInterval(() => {
          step++;
          const newVolume = Math.max(0, currentVolume - (volumeStep * step));
          player.setVolume(newVolume);

          if (step >= fadeSteps) {
            clearInterval(fadeInterval);
          }
        }, stepDuration);
      } catch (error) {
        // Silently handle any player errors
      }
    }

    // If tray is open, close it first, then close modal
    if (isTrayOpen) {
      // log('Closing tray before modal exit');
      setIsTrayOpen(false);

      // Coordinated timing sequence for tray close -> exit
      setTimeout(() => setShouldExit(true), ANIMATION_CONFIG.DUR_TRAY_OPEN * 1000);
      setTimeout(() => {
        setShowModal(false);
        onCloseComplete?.();
      }, ANIMATION_CONFIG.DUR_TRAY_OPEN * 1000 + MODAL_TIMINGS.MODAL_EXIT_WITH_TRAY_DURATION);
    }

    // Tray is already closed, start exit immediately
    else {
      setShouldExit(true);
      // Wait for fade animation (333ms), then actually close
      setTimeout(() => {
        setShowModal(false);
        onCloseComplete?.();
      }, MODAL_TIMINGS.MODAL_EXIT_FADE_DURATION);
    }
  };

  const handlePrevNext = (direction: 'prev' | 'next') => {
    log('handlePrevNext', { direction });

    let newIndex: number;
    let newVideo = null;
    let newChannel = activeChannel;

    // Previous
    if (direction === 'prev') {
      newIndex = currentVideoIndex - 1;

      // If we're at the first video of the channel, go to previous channel's last video
      if (newIndex < 0) {
        // TODO: Implement previous channel navigation if needed
        // log('At first video - previous channel navigation not implemented yet');
        return;
      }
    }

    // Next
    else {
      newIndex = currentVideoIndex + 1;

      // If we're at the last video of the channel, go to next channel
      if (newIndex >= channelVideos.length) {
        // log('Last video in channel - navigating to next channel');

        // Get next channel (with wrapping)
        let nextChannelIndex = currentChannelIndex + 1;
        if (nextChannelIndex >= channels.length) {
          // Wrap to first channel
          nextChannelIndex = 0;
          // log('Wrapping to first channel');
        }

        newChannel = channels[nextChannelIndex];
        const nextChannelVideos = ensurePostsAreRHTVVideos(newChannel?.rhtvChannelContent?.rhtvVideos);

        if (nextChannelVideos && nextChannelVideos.length > 0) {
          newVideo = nextChannelVideos[0]; // First video of next channel

          // Update active channel
          setActiveChannel(newChannel);

          // Store the next video and show transition
          setNextVideo(newVideo);
          setIsShowingTransition(true);
          setIsPlaying(null); // Reset to unknown - we don't know if YouTube will autoplay or be blocked
          setUserPausedVideo(false);
          setHasVideoStarted(false); // Reset video started state for new video
          userHasInteractedRef.current = false; // Reset user interaction flag

          /* Update the route to reflect the new channel and video
          updateRHTVRoute({ channelSlug: newChannel.slug, videoSlug: newVideo.slug }); */
          // Note: Route will be updated automatically when setActiveVideo is called after transition
        }
        return;
      }
    }

    // Within same channel bounds
    if (newIndex >= 0 && newIndex < channelVideos.length) {
      newVideo = channelVideos[newIndex];

      // Store the next video and show transition
      setNextVideo(newVideo);
      setIsShowingTransition(true);
      setIsPlaying(null); // Reset to unknown - we don't know if YouTube will autoplay or be blocked
      setUserPausedVideo(false); // Block interaction on new video
      setHasVideoStarted(false); // Reset video started state for new video
      userHasInteractedRef.current = false; // Reset user interaction flag

      /* Update the route to reflect the new video
      if (activeChannel) {
        updateRHTVRoute({ channelSlug: activeChannel.slug, videoSlug: newVideo.slug }); // (ROUTE-CHANGE)
      } */
      // Note: Route will be updated automatically when setActiveVideo is called after transition
    }
  };

  const handlePauseToggle = () => {
    // log('handlePauseToggle', { isPlaying });

    const player = ytRef.current?.getPlayer();
    if (!player) {
      // log('No player available');
      return;
    }

    // Do nothing if we don't know the video state yet (null = unknown)
    if (isPlaying === null) {
      // log('isPlaying is null - waiting for YouTube to determine state');
      return;
    }

    if (isPlaying) {
      player.pauseVideo();
      setIsPlaying(false);
      setUserPausedVideo(true); // User paused via our button - allow YT interaction
    } else {
      player.playVideo();
      setIsPlaying(true);
      setUserPausedVideo(false); // User resumed via our button - block YT interaction
    }
  };

  // Keyboard handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Prevent default for keys we handle
      if (['Space', 'ArrowLeft', 'ArrowRight', 'Escape'].includes(e.code)) {
        e.preventDefault();
      }
      // Whick key was pressed?
      switch (e.code) {
        case 'Space': {
          handlePauseToggle();
          break;
        }
        case 'ArrowLeft': {
          if (currentVideoIndex > 0) {
            handlePrevNext('prev');
          }
          break;
        }
        case 'ArrowRight': {
          // Always allow next (with channel wrapping)
          handlePrevNext('next');
          break;
        }
        case 'Escape': {
          handleClose();
          break;
        }
      }
    };

    // Add keyboard event listener
    window.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentVideoIndex, channelVideos.length]);

  //#endregion Handlers
  // -------------------- YouTube player
  //#region YouTube

  const autoplay = true;
  const ytRef = useRef<YTRef>(null);

  const handleYTStateChange = (event: YT.OnStateChangeEvent) => {
    // Log ALL state changes to debug
    const stateNames = {
      [-1]: 'UNSTARTED',
      [0]: 'ENDED',
      [1]: 'PLAYING',
      [2]: 'PAUSED',
      [3]: 'BUFFERING',
      [5]: 'CUED'
    };
    log(`YouTube state change: ${stateNames[event.data as keyof typeof stateNames] || event.data}`);
    
    // CRITICAL: Ignore all YouTube state changes during transition
    // The old player is still mounted and might fire events
    // We only want to respond to the NEW player's events after transition completes
    if (isShowingTransition) {
      log('Ignoring YouTube state change during transition');
      return;
    }

    // Check if video started playing
    if (event.data === YT.PlayerState.PLAYING) {
      // CRITICAL FIX FOR iOS: On touch devices, YouTube fires PLAYING even when autoplay is blocked
      // We can't trust this event OR getCurrentTime() - both lie on iOS
      // Only trust the event after user has actually clicked to start the video
      if (isTouchDevice) {
        if (userHasInteractedRef.current) {
          // User has clicked - trust this PLAYING event
          setIsPlaying(true);
          setHasVideoStarted(true);
          setUserPausedVideo(false);
        } else {
          // Ignore false PLAYING event from iOS autoplay block
          // Wait for actual user interaction before setting any state
        }
      } else {
        // Desktop: autoplay works reliably, trust the PLAYING event immediately
        setIsPlaying(true);
        setHasVideoStarted(true);
        setUserPausedVideo(false);
      }
    } else if (event.data === YT.PlayerState.PAUSED) {
      // If video paused, it must have started (can't pause what never played)
      // ALWAYS set hasVideoStarted - PAUSED is a trustworthy event
      setHasVideoStarted(true);
      setIsPlaying(false);
      // Note: We don't set userPausedVideo here because we only want to
      // allow interaction when paused via OUR button, not YouTube's controls
    } else if (event.data === YT.PlayerState.ENDED) {
      // Video ended - automatically go to next (with channel wrapping)
      handlePrevNext('next');
    } else if (event.data === -1) {
      // UNSTARTED means new video loaded - reset to blank slate
      setIsPlaying(null);
      setHasVideoStarted(false);
      setUserPausedVideo(false);
      userHasInteractedRef.current = false;
    }
  };

  //#endregion YouTube
  // -------------------- Transition video
  //#region Transition Video

  const transitionVideoRef = useGlobalVolume<HTMLVideoElement>('rhtv-transition-video');

  // Play transition video and switch to next video when it ends
  useEffect(() => {
    if (isShowingTransition && transitionVideoRef.current && nextVideo) {
      const transVideoEl = transitionVideoRef.current;

      // log('Playing transition video');

      // Stop the YouTube video immediately
      const player = ytRef.current?.getPlayer();
      if (player) {
        try {
          player.pauseVideo();
          // log('Paused YouTube video for transition');
        } catch (error) {
          log('Error pausing YouTube video:', error);
        }
      }

      // Ensure video starts from beginning
      transVideoEl.currentTime = 0;

      // Set lower volume for transition video (50% of global volume)
      // The volume of this was way too loud in general
      if (rrtvSoundEnabled) {
        transVideoEl.volume = (globalVolume * 0.5) * 0.4; // 50% of global volume, then reduced to 40%
      }

      // Play the transition video
      const playPromise = transVideoEl.play();

      if (playPromise !== undefined) {
        playPromise.catch(error => {
          log('Error playing transition video:', error);
          // If autoplay fails, skip transition and go straight to next video
          setIsShowingTransition(false);
          setActiveVideo(nextVideo);
          setNextVideo(null);
        });
      }

      // Handle when transition video ends
      const handleTransitionEnd = () => {
        // log('Transition video ended, switching to next video');
        setIsShowingTransition(false);
        setActiveVideo(nextVideo);
        setNextVideo(null);

        // Increment transition index and wrap around (0 -> 1 -> 2 -> 0)
        transitionIndexRef.current = (transitionIndexRef.current + 1) % TRANSITION_VIDEOS.length;
      };

      transVideoEl.addEventListener('ended', handleTransitionEnd);

      return () => {
        transVideoEl.removeEventListener('ended', handleTransitionEnd);
      };
    }
  }, [isShowingTransition, nextVideo]);

  //#endregion Transition Video
  // -------------------- Style Calculation Helpers

  // Calculate player mask styles (handles clipping when tray is open)
  const getPlayerMaskStyles = (): React.CSSProperties | undefined => {
    if (!playerDimensions) return undefined;

    // Both modes: Clip the player height when tray opens
    const clipHeight = (isTrayOpen && trayHeight > 0 && originalPlayerHeight)
      ? originalPlayerHeight - trayHeight
      : playerDimensions.height;

    return {
      width: `${playerDimensions.width}px`,
      height: `${playerDimensions.height}px`,
      '--clip-height': `${clipHeight}px`,
    } as React.CSSProperties;
  };

  // Calculate player dimensions styles
  const getPlayerStyles = (): React.CSSProperties | undefined => {
    if (!playerDimensions) return undefined;

    return {
      width: `${playerDimensions.width}px`,
      height: `${playerDimensions.height}px`,
    };
  };

  // -------------------- Pause Cover Helpers
  //#region Pause Cover Helpers

  // Determine if pause cover should be visible
  // IMPORTANT: Only show pause screen when we KNOW the video is paused (isPlaying === false)
  // Do NOT show when isPlaying is null (unknown state - waiting for YouTube)
  // Do NOT show during transition between videos
  const shouldShowPauseCover = () => {
    return isPlaying === false && hasVideoStarted && !isClosing && !isShowingTransition;
  };

  // Get pause cover opacity value
  const getPauseCoverOpacity = () => {
    return shouldShowPauseCover() ? 1 : 0;
  };

  // Get pause cover transition duration
  const getPauseCoverDuration = () => {
    return shouldShowPauseCover() ? 0 : MODAL_TIMINGS.PAUSE_SCREEN_FADE_OUT_DURATION / 1000;
  };

  // Get pause cover transition delay
  const getPauseCoverDelay = () => {
    return shouldShowPauseCover() ? 0 : MODAL_TIMINGS.PAUSE_SCREEN_FADE_OUT_DELAY / 1000;
  };

  // Get pause cover pointer events
  const getPauseCoverPointerEvents = () => {
    return shouldShowPauseCover() ? 'auto' : 'none';
  };

  // Get pause cover image opacity (0 when WebGL is active, 1 as fallback)
  const getPauseCoverImageOpacity = () => {
    return shouldShowPauseCover() ? 0 : 1;
  };

  //#endregion Pause Cover Helpers
  // -------------------- Measure content size and report to parent
  //#region Measure Content

  useEffect(() => {
    if (!contentRef.current || !playerRef.current) return;

    // Measure the actual rendered size and position of the modal content
    const measureContent = () => {
      if (!contentRef.current || !playerRef.current) return;

      // Get the available space in the content container
      const contentRect = contentRef.current.getBoundingClientRect();
      const availableWidth = contentRect.width;
      const availableHeight = contentRect.height;

      // 16:9 aspect ratio
      const aspectRatio = 16 / 9;

      let finalWidth: number;
      let finalHeight: number;

      // Try width-based sizing first (100% width)
      const widthBasedWidth = availableWidth;
      const widthBasedHeight = widthBasedWidth / aspectRatio;

      if (widthBasedHeight <= availableHeight) {
        // Width-based sizing fits! Use it.
        finalWidth = widthBasedWidth;
        finalHeight = widthBasedHeight;
        // log('Using width-based sizing (100% width)', { finalWidth, finalHeight });
      } else {
        // Width-based sizing is too tall, fall back to height-based sizing (100% height)
        finalHeight = availableHeight;
        finalWidth = finalHeight * aspectRatio;
        // log('Using height-based sizing (100% height)', { finalWidth, finalHeight });
      }

      // Calculate ceiled whole numbers to avoid sub-pixel rendering
      const ceiledWidth = Math.ceil(finalWidth);
      const ceiledHeight = Math.ceil(finalHeight);

      // log('Modal content dimensions:', {
      //   available: { width: availableWidth, height: availableHeight },
      //   calculated: { width: finalWidth, height: finalHeight },
      //   ceiled: { width: ceiledWidth, height: ceiledHeight }
      // });

      // Store original height for tray animation calculations
      if (!originalPlayerHeight) {
        setOriginalPlayerHeight(ceiledHeight);
      }

      // Set the ceiled dimensions to state for inline styling
      setPlayerDimensions({ width: ceiledWidth, height: ceiledHeight });

      // Wait for the dimensions to be applied and the element to be repositioned
      // Using double RAF to ensure React has updated and CSS has reflowed
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!playerRef.current) return;

          // Now get the position after the player has been sized and positioned
          const playerRect = playerRef.current.getBoundingClientRect();
          const { x, y } = playerRect;

          // log('Modal player position after resize:', { x, y, width: ceiledWidth, height: ceiledHeight });

          // Report ceiled dimensions and final position to parent if callback exists
          if (onContentSizeChange) {
            onContentSizeChange({ width: ceiledWidth, height: ceiledHeight, x, y });
          }
        });
      });
    };

    // Measure after a short delay to ensure CSS has applied
    const timeoutId = setTimeout(measureContent, MODAL_TIMINGS.CONTENT_MEASUREMENT_DELAY);

    // Also measure on window resize
    window.addEventListener('resize', measureContent);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', measureContent);
    };
  }, [onContentSizeChange]);

  //#endregion Measure Content
  // -------------------- Tray
  //#region Tray

  const [isTrayOpen, setIsTrayOpen] = useState(false);
  const [isTrayAnimationComplete, setIsTrayAnimationComplete] = useState(false);

  const handleToggleTray = () => {
    setIsTrayOpen(!isTrayOpen);
    // Reset animation complete state when toggling
    if (!isTrayOpen) {
      setIsTrayAnimationComplete(false);
    }
  };

  const handleContentClick = (e: React.MouseEvent) => {
    // Check if the click is on an artist link - if so, don't interfere
    const target = e.target as HTMLElement;
    const isArtistLink = target.closest('[data-artist-link="true"]');

    if (isArtistLink) {
      // Artist links handle their own propagation
      return;
    }

    // Close tray if it's open and user clicks outside of the tray
    if (isTrayOpen) {
      setIsTrayOpen(false);
    }
  };

  // Close tray when active video changes
  useEffect(() => {
    if (activeVideo) {
      setIsTrayOpen(false);
    }
  }, [activeVideo]);

  // Measure tray height on mount (tray is always rendered but hidden initially)
  useEffect(() => {
    if (trayRowWrapperRef.current && trayHeight === 0) {
      // Use double RAF to ensure tray is fully laid out before measuring
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (trayRowWrapperRef.current) {
            const height = trayRowWrapperRef.current.offsetHeight;
            setTrayHeight(height);
            // log('Tray height pre-measured on mount:', height);
          }
        });
      });
    }
  }, []); // Empty deps - measure once on mount

  //#endregion Tray
  // -------------------- Accessibility
  //#region Accessibility

  // Auto-focus first element when modal opens
  useEffect(() => {
    // SSR safety check
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    if (!contentRef.current) return;

    // Focus the first interactive element when modal opens
    const focusFirstElement = () => {
      const focusableElements = contentRef.current?.querySelectorAll(
        'button:not([disabled]):not([tabindex="-1"]), a[href]:not([tabindex="-1"])'
      );

      if (focusableElements && focusableElements.length > 0) {
        const firstElement = focusableElements[0] as HTMLElement;
        firstElement.focus();
      }
    };

    // Small delay to ensure modal animation has started
    const timer = setTimeout(focusFirstElement, 50);

    return () => clearTimeout(timer);
  }, []); // Run once on mount

  // Focus Trap for Modal
  useEffect(() => {
    // Trap focus within the modal when it's open (unless tray is open, then tray handles it)
    // Only run on client
    if (typeof document === 'undefined') return;
    if (!contentRef.current) return;

    // Accessibility: Trap focus within the modal
    const handleTabKey = (e: KeyboardEvent) => {
      // If tray is open, let VideoTray handle focus trapping
      if (isTrayOpen) return;

      // Only handle Tab key
      if (e.key !== 'Tab') return;

      // Get all focusable elements within the modal
      const focusableElements = contentRef.current?.querySelectorAll(
        'button:not([disabled]):not([tabindex="-1"]), a[href]:not([tabindex="-1"])'
      );

      if (!focusableElements || focusableElements.length === 0) return;

      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

      // If Shift+Tab on first element, go to last element
      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
      // If Tab on last element, go to first element
      else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', handleTabKey); // TODO: Gabe consider

    return () => {
      document.removeEventListener('keydown', handleTabKey); // TODO: Gabe consider
    };
  }, [isTrayOpen]);

  // Create announcement text for screen readers when video changes
  const videoAnnouncement = useMemo(() => {
    if (!videoTitle) return '';

    const artistNames = artists?.map(artist => {
      const [artistType] = artist.type;
      const isInductee = artistType === 'inductee';
      const inductee = artist.inductee?.node ? ensurePostsAreInductees({ nodes: [artist.inductee.node] })[0] : null;
      return isInductee ? inductee?.title : artist.name;
    }).filter(Boolean).join(', ');

    return artistNames
      ? `Now playing: ${videoTitle} by ${artistNames}`
      : `Now playing: ${videoTitle}`;
  }, [videoTitle, artists]);

  //#endregion Accessibility
  // -------------------- Render

  return (
    <div
      className={cx(["rock-hall-tv-modal", { "portrait-mode": isPortraitMode }, { debug: isDebugMode }])}
      ref={ref}
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-label={`${channelTitle || 'Video'} playlist`}
    >
      {/* Content */}
      <div className={cx("content")}
        ref={contentRef}
        style={{ '--modal-border-radius': borderRadius } as React.CSSProperties}
        onClick={(e) => {
          // Stop propagation to prevent modal backdrop from closing
          e.stopPropagation();
          // Handle content clicks (tray closing, etc)
          handleContentClick(e);
        }}>

        {/* Player mask - clips player when tray opens */}
        <div
          className={cx("player-mask")}
          style={getPlayerMaskStyles()}
        >
          <div className={cx("player")}
            ref={playerRef}
            style={getPlayerStyles()}
            onClick={() => {
              // CRITICAL: Mark that user has interacted with this video
              if (!userHasInteractedRef.current) {
                userHasInteractedRef.current = true;
                
                // On touch devices, set states immediately on first click
                // (YouTube may not fire reliable PLAYING event)
                if (isTouchDevice) {
                  setIsPlaying(true);
                  setHasVideoStarted(true);
                  setUserPausedVideo(false);
                  return; // Let YouTube handle the click to start playback
                }
              }
              
              // Only intercept clicks when video is currently playing
              if (isPlaying === true) {
                handlePauseToggle();
              }
              // Otherwise let YouTube handle the click
            }}
          >
            {/* Only render content once dimensions are calculated */}
            {playerDimensions && (
              <>

              {/* YouTube player */}
              <YoutubeEmbed
                key={youtubeVideoId}
                autoplay={autoplay}
                soundEnabled={rrtvSoundEnabled}
                className={cx("youtube-embed")}
                ref={ytRef}
                id={youtubeVideoId}
                showControls={false}
                blockMouseInteraction={hasVideoStarted}
                onStateChange={handleYTStateChange}
              />

              {/* Pause screen */}
              {/* Only show pause screen if video has started playing at least once */}
              {/* Note: TEMPORARILY DISABLE by setting false below */}
              {true && (
              <motion.div
                className={cx("pause-cover")}
                initial={{ opacity: 0 }}
                animate={{ opacity: getPauseCoverOpacity() }}
                transition={{
                  duration: getPauseCoverDuration(),
                  delay: getPauseCoverDelay(),
                }}
                style={{ pointerEvents: getPauseCoverPointerEvents() }}
                onClick={() => {
                  // Only allow clicking pause screen to resume when we're in a known state
                  // Don't allow clicking during unknown state (isPlaying === null)
                  if (isPlaying !== null) {
                    handlePauseToggle();
                  }
                }}
              >
                <img
                  ref={pauseImageRef}
                  src={PauseScreenImage.src}
                  alt=""
                  aria-hidden="true"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    // Hide original image when WebGL is active, show as fallback when not
                    opacity: getPauseCoverImageOpacity()
                  }}
                />

                {/* WebGL Distortion Effect - replaces the image */}
                <WebGLDistortion
                  isActive={shouldShowPauseCover()}
                  imageElement={pauseImageRef.current}
                  keepAnimatingDuringFadeOut={true}
                  className={cx("webgl-distortion")}
                  isTouchDevice={isTouchDevice}
                />
              </motion.div>
              )}

              {/* UI Controls */}
              {!isPortraitMode ? (
                // LANDSCAPE MODE: UI inside player
                <PlayerUI
                  channelTitle={channelTitle}
                  videoTitle={videoTitle}
                  artists={artists}
                  currentVideoIndex={currentVideoIndex}
                  totalVideos={channelVideos.length}
                  isPlaying={isPlaying}
                  trayHeight={trayHeight}
                  externalYoutubeLink={externalYoutubeLink}
                  isTrayOpen={isTrayOpen}
                  onClose={handleClose}
                  onPrevious={() => handlePrevNext('prev')}
                  onNext={() => handlePrevNext('next')}
                  onPauseToggle={handlePauseToggle}
                  isTouchDevice={isTouchDevice}
                />
              ) : null}

              {/* Transition video - plays between video changes */}
              {isShowingTransition && (
                <video
                  ref={transitionVideoRef}
                  className={cx("transition-video")}
                  src={TRANSITION_VIDEOS[transitionIndexRef.current]}
                  muted={!rrtvSoundEnabled}
                  playsInline
                />
              )}

            </>
          )}

          </div>
        </div>

        {/* PORTRAIT MODE: PlayerUI positioned outside player */}
        {isPortraitMode && playerDimensions && (
          <PlayerUI
            channelTitle={channelTitle}
            videoTitle={videoTitle}
            artists={artists}
            currentVideoIndex={currentVideoIndex}
            totalVideos={channelVideos.length}
            isPlaying={isPlaying}
            trayHeight={trayHeight}
            externalYoutubeLink={externalYoutubeLink}
            isTrayOpen={isTrayOpen}
            onClose={handleClose}
            onPrevious={() => handlePrevNext('prev')}
            onNext={() => handlePrevNext('next')}
            onPauseToggle={handlePauseToggle}
            isPortraitMode={true}
            isTouchDevice={isTouchDevice}
          />
        )}

        <VideoTray
          videos={channelVideos}
          trayButtonLabels={trayButtonLabels}
          rrtvAriaLabels={rrtvAriaLabels}
          activeVideoSlug={activeVideo?.slug}
          isOpen={isTrayOpen}
          isAnimationComplete={isTrayAnimationComplete}
          onToggle={handleToggleTray}
          onVideoSelect={setActiveVideo}
          onAnimationComplete={() => {
            if (isTrayOpen) {
              setIsTrayAnimationComplete(true);
            }
          }}
          trayRowWrapperRef={trayRowWrapperRef}
          isTouchDevice={isTouchDevice}
        />

      </div>

      {/* Screen reader live region for announcing video changes */}
      <div className="sr-only"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {videoAnnouncement}
      </div>
    </div>
  );

});

export default motion.create(RHTV_Modal);
